import { ErrorMessage, useFormikContext } from 'formik';
import { CloudUploadIcon } from 'lucide-react';

import { Label } from '@src/design-system/label';
import {
  Upload,
  UploadArea,
  UploadError,
  UploadTrigger,
} from '@src/design-system/upload';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { FileUploaded } from '@src/features/placements/pages/create/file-uploaded';
import { usePlacementsFileUploader } from '@src/features/placements/pages/create/helpers';

import { REMINDER_CHANNELS, type TReminder } from '@src/shared/types/reminder';

const CHANNELS_WITHOUT_ATTACHMENTS = [REMINDER_CHANNELS.sms] as string[];

function getAttachmentsChannel(values: unknown): string | undefined {
  const v = values as {
    channel?: string;
    notification_channel?: string;
  };
  return v.notification_channel ?? v.channel;
}

export const AttachmentsField = () => {
  const { onDrop, progressMap } = usePlacementsFileUploader();
  const { values } = useFormikContext<TReminder>();

  const channel = getAttachmentsChannel(values);
  if (channel && CHANNELS_WITHOUT_ATTACHMENTS.includes(channel)) return null;

  return (
    <>
      {!values.template && (
        <>
          <Label>Attachments</Label>
          <Upload
            accept={{}}
            maxFiles={5}
            maxSize={5 * 1024 * 1024}
            onDrop={(acceptedFiles) =>
              onDrop(acceptedFiles, values.attachments, 'attachments')
            }
          >
            <UploadArea className='border opacity-80 bg-primary-30 hover:opacity-100 transition-opacity text-gray-800 border-dashed border-gray-300 rounded-lg p-6'>
              <UploadTrigger className='gap-2 h-fit [&_svg]:shrink-0 cursor-pointer flex flex-col items-center justify-center'>
                <CloudUploadIcon size={32} />
                <p className='text-sm'>
                  <strong>Click to upload</strong> or drag and drop files
                </p>
              </UploadTrigger>
            </UploadArea>
            <UploadError />
          </Upload>
        </>
      )}
      <ErrorMessage
        name='attachments'
        component={CustomErrorMessage}
      />
      {!!values.attachments?.length && (
        <div className='flex flex-wrap gap-2 mt-6'>
          {values.attachments.map((image) => (
            <FileUploaded
              key={image.name}
              fieldName='attachments'
              values={values.attachments}
              file={image}
              progressValue={progressMap[image.name] || 0}
            />
          ))}
        </div>
      )}
    </>
  );
};
